import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { KhAudio } from 'app/core/audio/kh-audio';
import { UrlService } from 'app/core/http/url.service';
import { NewAnalyticsEvent } from 'app/shared/models/new-analytics-event.model';
import { IGameShowRunIdInfo } from 'app/teacher/gameshow/models/gameshow-run-id-info';
import { IGameShowRunModel } from 'app/teacher/gameshow/models/gameshow-run-model';
import { IGameShowRunSettings } from 'app/teacher/gameshow/models/gameshow-run-settings';
import { GameShowActivityModel } from 'app/teacher/models/gameshow-activity.model';
import { TEACHER_ROUTE } from 'app/teacher/routing-constants/teacher-routing.constant';
import { IGameShowQuestion } from '../models/gameshow-question';
import { ILeaderboardInfo } from '../models/leaderboard-info';

import { MacroData } from './../../models/macro-data.model';

@Injectable({
  providedIn: 'root'
})
export class GameShowService {
  private readonly httpClient = inject(HttpClient);
  private readonly urlService = inject(UrlService);
  private readonly router = inject(Router);

  public music: KhAudio | null = null;
  public gameShowRun: IGameShowRunModel | null = null;

  /**
   * Navigates to the gameshow launch screen for a "Measure Growth" flow.
   * Routes to the new gameshow settings page when the class has the NewGameShow
   * permission, otherwise to the legacy launch screen.
   */
  public navigateToLaunchLinked(classId: string, publicUnitId: string, baseActivityId: string): Observable<boolean> {
    return this.getIsNewGameShow(classId).pipe(
      switchMap((isNewGameShow) => {
        const route = isNewGameShow
          ? TEACHER_ROUTE.GAME_SHOW.SETTINGS_LINKED.route(classId, publicUnitId, baseActivityId)
          : TEACHER_ROUTE.GAMESHOW.LAUNCH_LINKED.route(classId, publicUnitId, baseActivityId);

        return this.router.navigate(route);
      })
    );
  }

  public setStyles(): void {
    const html = document.querySelector('html');
    html!.style.backgroundColor = '#1D53AD';

    const body = document.querySelector('body');
    body!.style.backgroundColor = '#1D53AD';
    body!.style.paddingTop = '20px';
    body!.style.paddingBottom = '125px';
  }

  public resetStyles(): void {
    const html = document.querySelector('html');
    html!.style.backgroundColor = '';

    const body = document.querySelector('body');
    body!.style.backgroundColor = '';
    body!.style.paddingTop = '';
    body!.style.paddingBottom = '';
  }

  public getGameShowSettings(classId: string, publicUnitId: string): Observable<IGameShowRunSettings> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowSettings', classId, publicUnitId);

    return this.httpClient.get<IGameShowRunSettings>(url);
  }

  public getGameShowRunId(publicUnitId: string, data: IGameShowRunSettings): Observable<IGameShowRunIdInfo> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', publicUnitId, 'Launch');

    return this.httpClient.post<IGameShowRunIdInfo>(url, data);
  }

  public getGameShowRunInfo(gameShowRunId: string, attemptNumber: number): Observable<IGameShowRunModel> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, 'Info', attemptNumber.toString());

    return this.httpClient.get<IGameShowRunModel>(url);
  }

  public getIsNewGameShow(classId: string): Observable<boolean> {
    const url = this.urlService.buildServerApiUrl('teacher', 'gameShowRun', classId, 'isNewGameShow');

    return this.httpClient.get<boolean>(url);
  }

  public getIsGameShowVerboseAnalytics(classId: string): Observable<boolean> {
    const url = this.urlService.buildServerApiUrl('teacher', 'gameShowRun', classId, 'isGameShowVerboseAnalytics');

    return this.httpClient.get<boolean>(url);
  }

  public deleteSolution(gameShowRunId: string, itemOrder: number, studentId: string): Observable<unknown> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, itemOrder.toString(), studentId);

    return this.httpClient.delete<unknown>(url);
  }

  public rotateSolution(gameShowRunId: string, itemOrder: number, studentId: string): Observable<unknown> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, itemOrder.toString(), studentId, 'RotateSolution');

    return this.httpClient.post<unknown>(url, {});
  }

  public rejectStudent(gameShowRunId: string, studentId: string): Observable<unknown> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, 'Reject', studentId);

    return this.httpClient.post<unknown>(url, {});
  }

  public getMacroData(gameShowRunId: string, itemOrder = 1): Observable<MacroData> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, 'MacroData', itemOrder.toString());

    return this.httpClient.get<MacroData>(url);
  }

  public getIntermediateLeaderboardInfo(gameShowRunId: string, itemOrder = 1): Observable<any> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, 'IntermediateLeaderboard', itemOrder.toString());

    return this.httpClient.post<any>(url, {});
  }

  public sendCloseQuestion(gameShowRunId: string, itemOrder = 1): Observable<any> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, 'CloseQuestion', itemOrder.toString());

    return this.httpClient.post<any>(url, {});
  }

  public getNextQuestion(gameShowRunId: string): Observable<IGameShowQuestion> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, 'NextQuestion');

    return this.httpClient.post<IGameShowQuestion>(url, {});
  }

  public sendCloseGameShow(gameShowRunId: string): Observable<any> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, 'Close');

    return this.httpClient.post<any>(url, {});
  }

  public finishGameShow(gameShowRunId: string): Observable<ILeaderboardInfo> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, 'Finish');

    return this.httpClient.post<ILeaderboardInfo>(url, {});
  }

  public getGameShowActivityInformation(gameShowRunId: string): Observable<GameShowActivityModel> {
    const url = this.urlService.buildServerApiUrl('Teacher/GameShowRun', gameShowRunId, 'Activity');

    return this.httpClient.get<GameShowActivityModel>(url);
  }

  public getTeacherGameShowLiveState(gameShowRunId: string, attemptNumber: number): Observable<any> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', gameShowRunId, 'Live', attemptNumber.toString());

    return this.httpClient.get<any>(url);
  }

  public getDateLive(): Observable<number> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', 'Live', 'Time');

    return this.httpClient.get<number>(url);
  }

  public sendInvites(gameShowRunId: string, classId: string): Observable<void> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', 'SendInvites');

    return this.httpClient.post<void>(url, { classId, gameShowRunId });
  }

  public gameShowCompletedEvent(newEvent: NewAnalyticsEvent): Observable<void> {
    const url = this.urlService.buildServerApiUrl('Teacher', 'GameShowRun', 'EndEvent');

    return this.httpClient.post<void>(url, newEvent);
  }
}
